import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Stack,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useEmbeddedAuth } from '@/contexts/EmbeddedAuth';
// @ts-ignore - lodash types not installed
import { debounce } from 'lodash';
import {
  searchAddresses,
  getBuildingFootprint,
  createDefaultFootprint,
} from '@/services/osm/osmService';
import {
  fetchOSMBuildingsGeoJSON,
  findClosestBuilding,
  mergeBuildingProperties,
} from '@/services/osm/osmBuildingsService';
import { generateBuilding, generateUnitLayout, generateUnitLayoutFromNYC, createBuildingGeometry, exportToGLB } from '@/services/buildingGenerator';
import { getNYCPropertyFromAddress, isNYCAddress, estimateFloorsFromUnits } from '@/services/nyc/nycPropertyService';
// @ts-ignore
import * as THREE from 'three';
import { NominatimResult, BuildingFootprintResult } from '@/types/osm';
import { NYCPropertyInfo } from '@/types/nyc';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { ModelPreview } from './ModelPreview';

interface QuickCreateFormProps {
  projectId: string; // Pre-generated project ID to use as modelId
  onComplete: (result: {
    modelUrl: string;
    buildingData: any;
    units: any[];
  }) => void;
  onCancel?: () => void;
}

const steps = ['Address', 'Building Info', 'Preview Units', 'Preview Model', 'Generate'];

export function QuickCreateForm({ projectId, onComplete, onCancel }: QuickCreateFormProps) {
  const { showAlert } = useAlert();

  // Step management
  const [activeStep, setActiveStep] = useState(0);

  // Address search
  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<NominatimResult[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<NominatimResult | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);

  // Get JWT from context
  const { jwt } = useEmbeddedAuth();

  // Building data
  const [buildingFootprint, setBuildingFootprint] = useState<BuildingFootprintResult | null>(null);
  const [buildingName, setBuildingName] = useState('');
  const [floors, setFloors] = useState<number>(4);
  const [totalUnits, setTotalUnits] = useState<number>(20);
  const [nycPropertyInfo, setNycPropertyInfo] = useState<NYCPropertyInfo | null>(null);
  const [loadingNYCData, setLoadingNYCData] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [previewUnits, setPreviewUnits] = useState<any[]>([]);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Debounced address search
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 3) {
        setAddressOptions([]);
        return;
      }

      setAddressLoading(true);
      try {
        const results = await searchAddresses(query);
        setAddressOptions(results);
      } catch (error) {
        console.error('Error searching addresses:', error);
        showAlert('Failed to search addresses', 'error');
      } finally {
        setAddressLoading(false);
      }
    }, 500),
    [showAlert]
  );

  useEffect(() => {
    if (addressQuery.length < 3) {
      setAddressOptions([]);
      return;
    }
    debouncedSearch(addressQuery);
  }, [addressQuery, debouncedSearch]);

  // Fetch building footprint when address is selected
  const handleAddressSelect = async (address: NominatimResult | null) => {
    setSelectedAddress(address);

    if (!address) {
      setBuildingFootprint(null);
      setNycPropertyInfo(null);
      return;
    }

    try {
      const lat = parseFloat(address.lat);
      const lon = parseFloat(address.lon);

      // Check if this is a NYC address and fetch NYC property data
      const isNYC = isNYCAddress(address.address);
      if (isNYC) {
        setLoadingNYCData(true);
        showAlert('Fetching NYC property data...', 'info');
        try {
          const nycData = await getNYCPropertyFromAddress(address.display_name);
          if (nycData) {
            setNycPropertyInfo(nycData);

            // Auto-fill building data from NYC portal
            if (nycData.condoInfo) {
              const condo = nycData.condoInfo;

              // Set total units from NYC data
              if (condo.totalUnits) {
                setTotalUnits(condo.totalUnits);
              }

              // Estimate floors if not available from OSM
              if (condo.totalUnits && !nycData.floors) {
                const estimatedFloors = estimateFloorsFromUnits(condo.totalUnits);
                setFloors(estimatedFloors);
              }

              showAlert(
                `NYC property data loaded! Found ${condo.totalUnits} units and ${condo.units.length} individual unit details.`,
                'success'
              );
            } else {
              showAlert('NYC property data found, but no condo information available.', 'warning');
            }
          } else {
            showAlert('No NYC property data found for this address.', 'warning');
          }
        } catch (nycError) {
          console.error('Error fetching NYC property data:', nycError);
          const errorMessage = (nycError as Error)?.message || '';
          if (errorMessage.includes('API key not configured')) {
            showAlert('NYC property data requires API key. Register at geoservice.planning.nyc.gov', 'info');
          } else {
            showAlert('Failed to fetch NYC property data. Continuing with OSM data only.', 'warning');
          }
        } finally {
          setLoadingNYCData(false);
        }
      }

      // Try to get building footprint from OSM
      const footprintResult = await getBuildingFootprint(lat, lon, 50);

      // Try to get additional data from OSMBuildings (optional - may fail)
      try {
        const osmBuildingsData = await fetchOSMBuildingsGeoJSON(lat, lon, 17);
        const closestBuilding = findClosestBuilding(osmBuildingsData, lat, lon);

        if (closestBuilding) {
          // Merge properties
          footprintResult.properties = mergeBuildingProperties(
            footprintResult.properties,
            closestBuilding.properties
          );

          // Use OSMBuildings footprint if OSM doesn't have one
          if (!footprintResult.footprint && closestBuilding.geometry.type === 'Polygon') {
            footprintResult.footprint = closestBuilding.geometry;
            footprintResult.found = true;
          }
        }
      } catch (osmBuildingsError) {
        // OSMBuildings is optional, continue without it
        console.warn('OSMBuildings data not available, using OSM data only:', osmBuildingsError);
      }

      // If no footprint found, create default
      if (!footprintResult.found || !footprintResult.footprint) {
        const defaultFootprint = createDefaultFootprint(lat, lon);
        setBuildingFootprint(defaultFootprint);
        showAlert(
          'No building footprint found in OSM. Using default rectangular footprint.',
          'warning'
        );
      } else {
        setBuildingFootprint(footprintResult);
        showAlert('Building footprint found from OpenStreetMap!', 'success');
      }

      // Pre-fill floors from OSM data if available
      if (footprintResult.properties.levels) {
        setFloors(footprintResult.properties.levels);
      } else if (footprintResult.properties.height) {
        // Estimate floors from height (3m per floor)
        setFloors(Math.round(footprintResult.properties.height / 3));
      }

      // Auto-advance to next step
      setActiveStep(1);
    } catch (error) {
      console.error('Error fetching building data:', error);
      showAlert('Failed to fetch building data', 'error');
    }
  };

  // Preview unit layout
  const handlePreview = () => {
    if (!buildingFootprint?.footprint) {
      showAlert('Building footprint is required', 'error');
      return;
    }

    // Calculate footprint area for bedroom estimation
    let footprintArea: number | undefined;
    if (buildingFootprint.footprint) {
      const coords =
        buildingFootprint.footprint.type === 'Polygon'
          ? buildingFootprint.footprint.coordinates[0]
          : buildingFootprint.footprint.coordinates[0][0];

      // Rough area calculation
      footprintArea = coords.length * 100; // Placeholder
    }

    // Use NYC unit data if available, otherwise generate generic units
    let units;
    if (nycPropertyInfo?.condoInfo?.units && nycPropertyInfo.condoInfo.units.length > 0) {
      units = generateUnitLayoutFromNYC(nycPropertyInfo.condoInfo.units, footprintArea);
      showAlert(`Using ${units.length} actual unit names from NYC Property Portal!`, 'success');
    } else {
      units = generateUnitLayout(floors, totalUnits, footprintArea);
    }

    setPreviewUnits(units);
    setActiveStep(2);
  };

  // Preview 3D model
  const handlePreviewModel = async () => {
    if (!buildingFootprint?.footprint) {
      showAlert('Building footprint is required', 'error');
      return;
    }

    setGenerating(true);

    try {
      // Calculate building height
      const floorHeight = 3; // meters
      const buildingHeight = floors * floorHeight;
      const unitsPerFloor = Math.ceil(totalUnits / floors);

      // Create 3D geometry
      const buildingObject = createBuildingGeometry(
        buildingFootprint.footprint,
        buildingHeight,
        floors,
        unitsPerFloor
      );

      // Create scene for preview
      const scene = new THREE.Scene();
      scene.add(buildingObject);

      // Add lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
      directionalLight.position.set(10, 10, 10);
      const lightTarget = new THREE.Object3D();
      lightTarget.position.set(0, 0, -1);
      directionalLight.add(lightTarget);
      directionalLight.target = lightTarget;
      scene.add(directionalLight);

      // Export to GLB
      const glbBlob = await exportToGLB(scene);
      setPreviewBlob(glbBlob);

      // Create object URL for preview
      const objectUrl = URL.createObjectURL(glbBlob);
      setPreviewUrl(objectUrl);

      setActiveStep(3);
    } catch (error) {
      console.error('Error generating preview:', error);
      showAlert('Failed to generate 3D preview', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Generate building (upload the preview)
  const handleGenerate = async () => {
    if (!previewBlob) {
      showAlert('Please preview the model first', 'error');
      return;
    }

    if (!jwt) {
      showAlert('Authentication token not available. Please try again.', 'error');
      return;
    }

    setGenerating(true);

    try {
      // Upload the already-generated GLB blob to embedded server using shared JWT
      const { uploadGLBToEmbeddedServer } = await import('@/services/buildingGenerator');

      const buildingNameId = buildingName || selectedAddress?.display_name || 'New Building';

      // Prepare metadata for the embedded server
      const metadata = {
        address: selectedAddress?.display_name || '',
        floors,
        totalUnits,
        coordinates: buildingFootprint!.coordinates,
        properties: buildingFootprint!.properties,
        apartmentPrefix: 'apt_',
        addOutline: true,
      };

      const result = await uploadGLBToEmbeddedServer(
        previewBlob,
        projectId, // Use projectId as modelId
        buildingNameId,
        jwt,
        metadata
      );

      showAlert(`Building uploaded successfully! File ID: ${result.fileId}`, 'success');

      // Prepare building data with the storage path from embedded server
      const buildingData = {
        name: buildingNameId,
        address: selectedAddress?.display_name || '',
        floors,
        modelPath: result.storagePath, // Use the GCS storage path
        coordinates: buildingFootprint!.coordinates,
        properties: buildingFootprint!.properties,
        fileId: result.fileId,
      };

      onComplete({
        modelUrl: result.storagePath,
        buildingData,
        units: previewUnits,
      });
    } catch (error) {
      console.error('Error uploading building:', error);
      showAlert(`Failed to upload building: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Search for Building Address
            </Typography>
            <Autocomplete
              freeSolo
              options={addressOptions}
              filterOptions={(x) => x}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.display_name
              }
              loading={addressLoading}
              value={selectedAddress}
              inputValue={addressQuery}
              onChange={(_, newValue) => {
                if (typeof newValue !== 'string') {
                  handleAddressSelect(newValue);
                }
              }}
              onInputChange={(_, newValue, reason) => {
                // Only update query if user is typing, not when selecting
                if (reason === 'input') {
                  setAddressQuery(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Address"
                  placeholder="Enter building address..."
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addressLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedAddress && !loadingNYCData && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected: {selectedAddress.display_name}
              </Alert>
            )}
            {loadingNYCData && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <span>Loading NYC property data...</span>
                </Stack>
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Building Information
            </Typography>
            <Stack spacing={3}>
              <TextField
                label="Building Name"
                value={buildingName}
                onChange={(e) => setBuildingName(e.target.value)}
                fullWidth
                placeholder="Optional"
              />
              <TextField
                label="Number of Floors"
                type="number"
                value={floors}
                onChange={(e) => setFloors(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 50 }}
                fullWidth
                required
              />
              <TextField
                label="Total Number of Units"
                type="number"
                value={totalUnits}
                onChange={(e) => setTotalUnits(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 1000 }}
                fullWidth
                required
              />
              {loadingNYCData && (
                <Alert severity="info">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <span>Loading NYC property data...</span>
                  </Stack>
                </Alert>
              )}
              {nycPropertyInfo?.condoInfo && (
                <Alert severity="success">
                  <Typography variant="subtitle2" gutterBottom>
                    NYC Property Data Found!
                  </Typography>
                  <Typography variant="body2">
                    • Total Units: {nycPropertyInfo.condoInfo.totalUnits}
                    {nycPropertyInfo.condoInfo.residentialUnits && (
                      <> (Residential: {nycPropertyInfo.condoInfo.residentialUnits})</>
                    )}
                    {nycPropertyInfo.condoInfo.commercialUnits && nycPropertyInfo.condoInfo.commercialUnits > 0 && (
                      <> (Commercial: {nycPropertyInfo.condoInfo.commercialUnits})</>
                    )}
                  </Typography>
                  <Typography variant="body2">
                    • Condo BBL: {nycPropertyInfo.condoInfo.condoBillingBBL}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {nycPropertyInfo.condoInfo.units.length} individual units found from NYC Property Portal
                  </Typography>
                </Alert>
              )}
              {buildingFootprint?.found === false && (
                <Alert severity="warning">
                  Using default footprint. Building will be a simple rectangular shape.
                </Alert>
              )}
              {buildingFootprint?.properties.height && (
                <Alert severity="info">
                  Building height from OSM: {buildingFootprint.properties.height}m
                  (≈{Math.round(buildingFootprint.properties.height / 3)} floors)
                </Alert>
              )}
            </Stack>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview Unit Layout
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {previewUnits.length} units across {floors} floors
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 2,
                mt: 2,
              }}
            >
              <Stack spacing={1}>
                {previewUnits.map((unit) => (
                  <Box
                    key={unit.apartmentId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Typography variant="body2">
                      Unit {unit.apartmentId} (Floor {unit.floor}, Line {unit.line})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {unit.numBedrooms}BR / {unit.numBathrooms}BA
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              3D Model Preview
            </Typography>
            {previewUrl ? (
              <Box>
                <Paper
                  variant="outlined"
                  sx={{
                    height: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    mb: 2,
                    overflow: 'hidden',
                  }}
                >
                  <ModelPreview modelUrl={previewUrl} width={600} height={400} />
                </Paper>
                <Alert severity="info">
                  3D model generated successfully! Review the model above, then click "Upload & Create" to save.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  File size: {(previewBlob!.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Generating 3D model preview...
                </Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Quick Create Building
      </Typography>

      <Stepper activeStep={activeStep} sx={{ my: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {renderStepContent()}
      </Paper>

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button variant="outlined" onClick={onCancel} disabled={generating}>
          Cancel
        </Button>

        <Stack direction="row" spacing={2}>
          {activeStep > 0 && activeStep < 4 && (
            <Button
              variant="outlined"
              onClick={() => setActiveStep(activeStep - 1)}
              disabled={generating}
            >
              Back
            </Button>
          )}

          {activeStep === 1 && (
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!buildingFootprint || floors < 1 || totalUnits < 1}
            >
              Preview Units
            </Button>
          )}

          {activeStep === 2 && (
            <Button
              variant="contained"
              onClick={handlePreviewModel}
              disabled={generating || previewUnits.length === 0}
            >
              {generating ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Generating Preview...
                </>
              ) : (
                'Preview 3D Model'
              )}
            </Button>
          )}

          {activeStep === 3 && (
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || !previewBlob}
              color="success"
            >
              {generating ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Uploading...
                </>
              ) : (
                'Upload & Create Building'
              )}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
